import Quill from 'quill';
import QuillTableBetter from 'quill-table-better';
import 'quill/dist/quill.snow.css';
import 'quill-table-better/dist/quill-table-better.css';
import '../../css/vendor/quill.scss';

function hasOldTableFormat(delta) {
  if (!delta || !delta.ops) {
    return false;
  }

  return delta.ops.some(op => op.attributes && op.attributes['table-cell-line']);
}

function hasNewTableFormat(delta) {
  if (!delta || !delta.ops) {
    return false;
  }

  return delta.ops.some(op => op.attributes && op.attributes['table-cell-block']);
}

function deltaToHtmlWithNewTables(delta) {
  const ops = delta.ops;
  const segments = [];
  let current = { type: 'content', ops: [] };

  function isStructuralAttr(attrs) {
    return !!(attrs && (attrs['table-temporary'] || attrs['table-col'] || attrs['table-cell-block']));
  }

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    const isTableTemp = op.attributes && op.attributes['table-temporary'];
    const isTableCol = op.attributes && op.attributes['table-col'];
    const isTableCell = op.attributes && op.attributes['table-cell-block'];

    if (isTableTemp || isTableCol || isTableCell) {
      if (current.type !== 'table') {

        const cellTextOps = [];

        if (isTableCell) {
          while (current.ops.length) {
            const last = current.ops[current.ops.length - 1];

            if (typeof last.insert === 'string' && !last.insert.includes('\n')) {
              cellTextOps.unshift(current.ops.pop());
            } else {
              break;
            }
          }
        }

        if (current.ops.length) {
          segments.push(current);
        }

        current = { type: 'table', tableAttrs: null, cols: [], rows: {}, rowOrder: [], cellTexts: [], cellOps: [], ops: [] };

        for (const textOp of cellTextOps) {
          current.cellTexts.push(textOp.insert);
          current.cellOps.push(textOp);
          current.ops.push(textOp);
        }
      }

      if (isTableTemp) {
        current.tableAttrs = op.attributes['table-temporary'];
        current.ops.push(op);
      } else if (isTableCol) {
        current.cols.push(op.attributes['table-col']);
        current.ops.push(op);
      } else if (isTableCell) {
        const cellInfo = op.attributes['table-cell'] || {};
        const rowId = cellInfo['data-row'] || '0';

        if (!current.rows[rowId]) {
          current.rows[rowId] = [];
          current.rowOrder.push(rowId);
        }

        current.rows[rowId].push({
          ops: current.cellOps.slice(),
          rowStyle: cellInfo.style || null
        });
        current.cellTexts = [];
        current.cellOps = [];
        current.ops.push(op);
      }
    } else if (current.type === 'table' && typeof op.insert === 'string' && op.insert !== '\n') {
      current.cellTexts.push(op.insert);
      current.cellOps.push(op);
      current.ops.push(op);
    } else {
      if (current.type === 'table') {

        const spilledOps = [];

        if (current.cellTexts.length) {
          while (current.ops.length) {
            const last = current.ops[current.ops.length - 1];

            if (isStructuralAttr(last.attributes)) {
              break;
            }

            spilledOps.unshift(current.ops.pop());
          }

          current.cellTexts = [];
          current.cellOps = [];
        }

        if (current.ops.length) {
          segments.push(current);
        }

        current = { type: 'content', ops: spilledOps };
      }

      current.ops.push(op);
    }
  }

  if (current.type === 'table' && current.cellTexts.length) {
    const spilledOps = [];

    while (current.ops.length) {
      const last = current.ops[current.ops.length - 1];

      if (isStructuralAttr(last.attributes)) {
        break;
      }

      spilledOps.unshift(current.ops.pop());
    }

    if (current.ops.length) {
      segments.push(current);
    }

    if (spilledOps.length) {
      segments.push({ type: 'content', ops: spilledOps });
    }
  } else if (current.ops.length) {
    segments.push(current);
  }

  let html = '';

  for (const segment of segments) {
    if (segment.type === 'table') {
      html += buildNewTableHtml(segment);
    } else {
      html += renderContentOps(segment.ops);
    }
  }

  return html;
}

function buildNewTableHtml(segment) {
  const attrs = segment.tableAttrs;
  const style = attrs && attrs.style ? ` style="${escapeHtml(attrs.style)}"` : '';
  const cls = attrs && attrs['data-class'] ? ` class="${escapeHtml(attrs['data-class'])}"` : ' class="ql-table-better"';
  let html = `<table${cls}${style}>`;

  if (segment.cols && segment.cols.length) {
    html += '<colgroup>';

    for (const col of segment.cols) {
      const w = col.width ? ` width="${escapeHtml(col.width)}"` : '';
      html += `<col${w}>`;
    }

    html += '</colgroup>';
  }

  html += '<tbody>';

  for (const rowId of segment.rowOrder) {
    const cells = segment.rows[rowId];
    const rowStyle = cells[0] && cells[0].rowStyle ? ` style="${escapeHtml(cells[0].rowStyle)}"` : '';
    html += `<tr${rowStyle}>`;

    for (const cell of cells) {
      html += `<td><p>${renderCellInlineHtml(cell.ops)}</p></td>`;
    }

    html += '</tr>';
  }

  html += '</tbody></table>';

  return html;
}

function deltaToHtmlWithTables(delta) {
  const ops = delta.ops;
  const segments = [];
  let current = { type: 'content', ops: [] };

  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    const isTableCol = op.attributes && op.attributes['table-col'];
    const isTableCell = op.attributes && op.attributes['table-cell-line'];

    if (isTableCol || isTableCell) {
      if (current.type !== 'table') {
        if (current.ops.length) {
          segments.push(current);
        }

        current = { type: 'table', cols: [], rows: {}, cellTexts: [], cellOps: [], ops: [] };
      }

      if (isTableCol) {
        current.cols.push(op.attributes['table-col']);
      } else if (isTableCell) {
        const cellInfo = op.attributes['table-cell-line'];
        const rowId = cellInfo.row || '0';
        const cellId = cellInfo.cell || '0';
        const align = op.attributes.align || null;

        if (!current.rows[rowId]) {
          current.rows[rowId] = [];
        }

        current.rows[rowId].push({
          cell: cellId,
          ops: current.cellOps.slice(),
          align: align
        });
        current.cellTexts = [];
        current.cellOps = [];
      }

      current.ops.push(op);
    } else if (current.type === 'table' && typeof op.insert === 'string' && op.insert !== '\n') {
      current.cellTexts.push(op.insert);
      current.cellOps.push(op);
      current.ops.push(op);
    } else {
      if (current.type === 'table') {
        const spilledOps = [];

        if (current.cellTexts.length) {
          while (current.ops.length) {
            const last = current.ops[current.ops.length - 1];

            if (last.attributes && (last.attributes['table-cell-line'] || last.attributes['table-col'])) {
              break;
            }

            spilledOps.unshift(current.ops.pop());
          }

          current.cellTexts = [];
          current.cellOps = [];
        }

        if (current.ops.length) {
          segments.push(current);
        }

        current = { type: 'content', ops: spilledOps };
      }

      current.ops.push(op);
    }
  }

  if (current.type === 'table' && current.cellTexts.length) {
    const spilledOps = [];

    while (current.ops.length) {
      const last = current.ops[current.ops.length - 1];

      if (last.attributes && (last.attributes['table-cell-line'] || last.attributes['table-col'])) {
        break;
      }

      spilledOps.unshift(current.ops.pop());
    }

    if (current.ops.length) {
      segments.push(current);
    }

    if (spilledOps.length) {
      segments.push({ type: 'content', ops: spilledOps });
    }
  } else if (current.ops.length) {
    segments.push(current);
  }

  let html = '';

  for (const segment of segments) {
    if (segment.type === 'table') {
      html += buildTableHtml(segment);
    } else {
      html += renderContentOps(segment.ops);
    }
  }

  return html;
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;

  return div.innerHTML;
}

function normalizeListsInHtml(rootElement) {
  rootElement.querySelectorAll('ol').forEach((ol) => {
    const items = Array.from(ol.children);
    const groups = [];
    let currentGroup = null;

    for (const li of items) {
      const tag = li.dataset.list === 'bullet' ? 'ul' : 'ol';

      if (!currentGroup || currentGroup.tag !== tag) {
        currentGroup = { tag: tag, items: [] };
        groups.push(currentGroup);
      }

      currentGroup.items.push(li);
    }

    if (groups.length === 1 && groups[0].tag === 'ol') {
      groups[0].items.forEach((li) => li.removeAttribute('data-list'));

      return;
    }

    const parent = ol.parentNode;

    for (const group of groups) {
      const list = document.createElement(group.tag);

      for (const li of group.items) {
        li.removeAttribute('data-list');
        list.appendChild(li);
      }

      parent.insertBefore(list, ol);
    }

    parent.removeChild(ol);
  });
}

function buildTableHtml(segment) {
  let html = '<table class="ql-table-better">';

  if (segment.cols.length) {
    html += '<colgroup>';

    for (const col of segment.cols) {
      const w = col.width ? ` width="${escapeHtml(col.width)}"` : '';
      html += `<col${w}>`;
    }

    html += '</colgroup>';
  }

  html += '<tbody>';
  const rowIds = Object.keys(segment.rows).sort((a, b) => Number(a) - Number(b));

  for (const rowId of rowIds) {
    html += '<tr>';
    const cells = segment.rows[rowId].sort((a, b) => Number(a.cell) - Number(b.cell));

    for (const cell of cells) {
      html += '<td>';
      const alignStyle = cell.align ? ` style="text-align:${escapeHtml(cell.align)}"` : '';
      html += `<p${alignStyle}>${renderCellInlineHtml(cell.ops)}</p>`;
      html += '</td>';
    }

    html += '</tr>';
  }

  html += '</tbody></table>';

  return html;
}

let cellQuillContainer = null;
let cellQuillInstance = null;

function renderCellInlineHtml(ops) {
  if (!ops || !ops.length) {
    return '';
  }

  if (!cellQuillInstance) {
    cellQuillContainer = document.createElement('div');
    cellQuillInstance = new Quill(cellQuillContainer, {
      modules: { table: false, toolbar: false }
    });
  }

  cellQuillInstance.setContents({ ops: [...ops, { insert: '\n' }] });
  const editor = cellQuillContainer.getElementsByClassName('ql-editor')[0];

  return Array.from(editor.children).map(block => block.innerHTML).join('<br>');
}

function renderContentOps(ops) {
  if (!ops.length) {
    return '';
  }

  const last = ops[ops.length - 1];

  if (typeof last.insert !== 'string' || !last.insert.endsWith('\n')) {
    ops = [...ops, { insert: '\n' }];
  }

  const container = document.createElement('div');
  const quill = new Quill(container, {
    modules: { table: false, toolbar: false }
  });
  quill.setContents({ ops });

  return container.getElementsByClassName('ql-editor')[0].innerHTML;
}

const Block = Quill.import('blots/block');
const Inline = Quill.import('blots/inline');
const Font = Quill.import('formats/font');
const BlockEmbed = Quill.import('blots/block/embed');
const Link = Quill.import('formats/link');
const Header = Quill.import('formats/header');
const icons = Quill.import('ui/icons');
const Delta = Quill.import('delta');
const Image = Quill.import('formats/image');

class Button extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute('class', this.className);

    return node;
  }

  static formats(domNode) {
    return true;
  }
}
Button.blotName = 'button';
Button.className = 'ql-button';

class TwoColumns extends Block {
  static create() {
    const node = super.create();
    node.setAttribute('class', this.className);

    return node;
  }

  static formats(domNode) {
    return true;
  }
}
TwoColumns.blotName = 'two-columns';
TwoColumns.className = 'ql-two-columns';
TwoColumns.tagName = 'div';

class ColumnLeft extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute('class', this.className);

    return node;
  }

  static formats(domNode) {
    return true;
  }
}
ColumnLeft.blotName = 'col-l';
ColumnLeft.className = 'ql-col-l';

class ColumnRight extends Inline {
  static create() {
    const node = super.create();
    node.setAttribute('class', this.className);

    return node;
  }

  static formats(domNode) {
    return true;
  }
}
ColumnRight.blotName = 'col-r';
ColumnRight.className = 'ql-col-r';

class MyLink extends Link {
  static create(value) {
    const node = super.create(value);
    value = this.sanitize(value);

    if (value.match(/^\d{9}$/)) {
      node.setAttribute('href', `tel:${value}`);
      node.className = 'link--tel';
    }

    if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(value)) {
      node.setAttribute('href', `mailto:${value}`);
      node.className = 'link--mail';
    }

    if (value.startsWith('https://') || value.startsWith('http://')) {
      node.className = 'link--external';
    } else {
      node.removeAttribute('target');
    }

    return node;
  }
}

class Anchor extends Block {
  static create(value) {
    const node = super.create(value);
    node.setAttribute('class', 'anchor');

    return node;
  }
}
Anchor.blotName = 'anchor';
Anchor.tagName = 'div';

Font.whitelist = ['Roboto', 'sans-serif'];

class ImageWithFilename extends Image {
  static get ATTRIBUTES() {
    return ['alt', 'height', 'width', 'class', 'data-filename'];
  }

  static formats(domNode) {
    return this.ATTRIBUTES.reduce((formats, attr) => {
      if (domNode.hasAttribute(attr)) {
        formats[attr] = domNode.getAttribute(attr);
      }

      return formats;
    }, {});
  }

  format(name, value) {
    if (this.constructor.ATTRIBUTES.indexOf(name) > -1) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }
}

Quill.register(ImageWithFilename, true);

Quill.register(Button);
Quill.register(Font, true);
Quill.register(TwoColumns);
Quill.register(ColumnLeft);
Quill.register(ColumnRight);
Quill.register(Anchor);
Quill.register(MyLink);

Quill.register({
  'modules/table-better': QuillTableBetter
}, true);

//toolbar icons
icons['button'] = 'btn';
icons['two-columns'] = 'cols';
icons['col-l'] = 'col-l';
icons['col-r'] = 'col-r';
icons['anchor'] = 'kotva';

class CustomQuill {
  constructor(toolbarOptions, uploadConfig = null) {
    this.upload = Object.assign({
      url: null,
      paramName: 'image',
      headers: {},
      withCredentials: true,
      extra: {},
      mode: 'deferred'
    }, uploadConfig || {});

    if (toolbarOptions) {
      this.options = {
        modules: {
          toolbar: toolbarOptions,
          table: false,
          'table-better': {},
          keyboard: {
            bindings: QuillTableBetter.keyboardBindings
          }
        },
        theme: 'snow',
        placeholder: 'Nový příspěvek...'
      };
    } else {
      this.tempContainer = document.createElement('div');
    }
  }

  createEditor(editor) {
    this.editor = new Quill(editor, this.options);
    this.quill = this.editor;

    const toolbar = this.editor.getModule('toolbar');

    if (toolbar) {
      toolbar.addHandler('image', () => this.selectLocalImage());

      toolbar.addHandler('table', () => {
        const bt = this.editor.getModule('table-better');
        if (!bt) {
          alert('Modul table-better není dostupný.');
          return;
        }

        bt.insertTable(3, 3);
      });
    }

    this.editor.root.addEventListener('paste', (e) => this.handlePaste(e), { capture: true });
    this.editor.root.addEventListener('drop', (e) => this.handleDrop(e), { capture: true });
    this.editor.root.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    }, { capture: true });
  }

  bindToForm(input, form) {
    const textInputSelector = document.querySelector(input);
    const formSelector = document.querySelector(form);

    if (textInputSelector.value !== '') {
      const parsed = JSON.parse(textInputSelector.value);

      if (hasOldTableFormat(parsed)) {
        const html = deltaToHtmlWithTables(parsed);
        const converted = this.editor.clipboard.convert({ html });
        this.editor.updateContents(converted, Quill.sources.USER);
      } else {
        this.editor.updateContents(parsed);
      }
    }

    formSelector.addEventListener('submit', async (e) => {
      let delta = this.editor.getContents();
      delta = await this.replaceBase64ImagesInDelta(delta);
      textInputSelector.value = JSON.stringify(delta);
      formSelector.submit();
    });
  }

  fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = () => reject(new Error('Čtení souboru selhalo'));
      fr.readAsDataURL(file);
    });
  }

  async insertLocalFile(file, filenameHint = 'image.png') {
    if (this.upload.mode === 'eager') {
      const url = await this.uploadImageFile(file, filenameHint);
      this.insertToEditor(url, file?.name || filenameHint);
    } else {
      const dataUrl = await this.fileToDataUrl(file);
      this.insertToEditor(dataUrl, file?.name || filenameHint);
    }
  }

  selectLocalImage() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.onchange = async () => {
      const file = input.files && input.files[0];

      if (!file) {
        return;
      }

      try {
        await this.insertLocalFile(file, file.name);
      } catch (err) {
        console.error(err);
        alert('Nahrání obrázku selhalo.');
      }
    };

    input.click();
  }

  async handlePaste(e) {
    const items = (e.clipboardData || window.clipboardData)?.items;

    if (!items) {
      return;
    }

    const hasImage = Array.from(items).some(it => it.type?.startsWith('image'));
    const hasHtml  = Array.from(cd.types || []).includes('text/html');
    const hasText  = Array.from(cd.types || []).includes('text/plain');

    if (!hasImage) {
      return;
    }

    if (hasImage) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      for (let i = 0; i < items.length; i++) {
        const it = items[i];

        if (it.type && it.type.indexOf('image') === 0) {
          e.preventDefault();
          const file = it.getAsFile();

          try {
            await this.insertLocalFile(file, file.name || 'clipboard.png');
          } catch (err) {
            console.error(err);
            alert('Nahrání obrázku z clipboardu selhalo.');
          }
        }
      }
    }
  }

  async handleDrop(e) {
    e.preventDefault();
    const files = e.dataTransfer?.files;

    if (!files || !files.length) {
      return;
    }

    for (const file of files) {
      if (!file.type || file.type.indexOf('image') !== 0) {
        continue;
      }

      try {
        await this.insertLocalFile(file, file.name);
      } catch (err) {
        console.error(err);
        alert('Nahrání obrázku (drop) selhalo.');
      }
    }
  }

  insertToEditor(url, filename = null) {
    const range = this.editor.getSelection(true);
    const index = range ? range.index : this.editor.getLength();
    this.editor.insertEmbed(index, 'image', url, 'user');

    if (filename) {
      this.editor.formatText(index, 1, { 'data-filename': filename });
    }

    this.editor.setSelection(index + 1, 'silent');
  }

  dataURLtoFile(dataUrl, filename) {
    const [meta, data] = dataUrl.split(',');
    const mime = (meta.match(/data:(.*?);/) || [])[1] || 'image/png';
    const binary = atob(data);
    const len = binary.length;
    const u8 = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      u8[i] = binary.charCodeAt(i);
    }

    return new File([u8], filename, { type: mime });
  }

  async replaceBase64ImagesInDelta(delta) {
    if (this.upload.mode === 'deferred') {
      return delta;
    }

    const ops = delta.ops ? [...delta.ops] : [];

    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      if (op.insert && op.insert.image && typeof op.insert.image === 'string' && op.insert.image.startsWith('data:')) {
        const file = this.dataURLtoFile(op.insert.image, 'inline.png');
        const url = await this.uploadImageFile(file, 'inline.png');
        op.insert.image = url;
      }
    }

    return new Delta({ ops });
  }

  async uploadImageFile(file, filenameHint = 'image.png') {
    if (!this.upload.url) {
      throw new Error('Upload URL není nastaven.');
    }

    const fd = new FormData();
    fd.append(this.upload.paramName || 'image', file, filenameHint);
    Object.entries(this.upload.extra || {}).forEach(([k, v]) => fd.append(k, v));

    let res;

    try {
      res = await fetch(this.upload.url, {
        method: 'POST',
        body: fd,
        headers: this.upload.headers || {},
        credentials: this.upload.withCredentials ? 'include' : 'same-origin'
      });
    } catch (netErr) {
      throw new Error(`Network error: ${netErr.message}`);
    }

    const raw = await res.text().catch(() => '');

    if (!res.ok) {
      throw new Error(`HTTP ${res.status} ${res.statusText} – ${raw}`);
    }

    let json;

    try {
      json = raw ? JSON.parse(raw) : {};
    } catch (e) {
      throw new Error(`Invalid JSON: ${raw.slice(0, 200)}`);
    }

    if (!json.url) {
      throw new Error(`Server nevrátil url. Odpověď: ${raw.slice(0, 200)}`);
    }

    return json.url;
  }

  showHtml(inputDelta, destElement) {
    const destElementSelector = typeof destElement === 'string' ? document.querySelector(destElement) : destElement;
    const parsed = JSON.parse(inputDelta);
    let outputHtml;

    if (hasOldTableFormat(parsed)) {
      outputHtml = deltaToHtmlWithTables(parsed);
    } else if (hasNewTableFormat(parsed)) {
      outputHtml = deltaToHtmlWithNewTables(parsed);
    } else {
      const quill = new Quill(this.tempContainer, {
        modules: { table: false, toolbar: false }
      });
      quill.setContents(parsed);
      outputHtml = this.tempContainer.getElementsByClassName('ql-editor')[0].innerHTML;
      this.tempContainer.innerHTML = '';
    }

    const fragment = document.createRange().createContextualFragment(outputHtml);

    normalizeListsInHtml(fragment);

    fragment.querySelectorAll('.anchor').forEach((anchor) => {
      const id = anchor.innerText;
      anchor.setAttribute('id', id.trim());
      anchor.innerText = '';
    });

    fragment.querySelectorAll('table').forEach((table) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'table-responsive';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    destElementSelector.appendChild(fragment);
  }
}

document.querySelectorAll('[data-quill-content]').forEach((el) => {
  const quill = new CustomQuill();
  quill.showHtml(el.dataset.quillContent, el);
  el.removeAttribute('data-quill-content');
});

window.CustomQuill = CustomQuill;
